/**
 * Form primitives, shared across modules.
 *
 * Worth keeping together: a 44px tap target, a
 * visible label rather than a placeholder standing in for one, and a required
 * marker that is never colour alone are decisions we want to make once.
 */

import { useId } from 'react'

const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: 'var(--t-sm)', fontWeight: 600,
  color: 'var(--ink-2)', marginBottom: 'var(--s-1)',
}
const inputStyle: React.CSSProperties = {
  width: '100%', minHeight: 'var(--tap)', padding: 'var(--s-2) var(--s-3)',
  border: '1px solid var(--line)', borderRadius: 'var(--radius-sm)',
  font: 'inherit', fontSize: 'var(--t-md)', background: 'var(--surface)',
}
const wrap: React.CSSProperties = { marginBottom: 'var(--s-4)' }

function Req({ on }: { on?: boolean }) {
  return on ? <span aria-hidden="true" style={{ color: 'var(--critical)' }}> *</span> : null
}

export function Text(p: {
  label: string; value: string; onChange: (v: string) => void
  placeholder?: string; required?: boolean; type?: string; min?: string | number; help?: string
}) {
  const id = useId()
  return (
    <div style={wrap}>
      <label style={labelStyle} htmlFor={id}>{p.label}<Req on={p.required} /></label>
      <input id={id} style={inputStyle} value={p.value} placeholder={p.placeholder} required={p.required}
        type={p.type ?? 'text'} min={p.min}
        onChange={(e) => p.onChange(e.target.value)} />
      {p.help && <p style={helpStyle}>{p.help}</p>}
    </div>
  )
}

export function Area(p: {
  label: string; value: string; onChange: (v: string) => void
  placeholder?: string; required?: boolean
}) {
  const id = useId()
  return (
    <div style={wrap}>
      <label style={labelStyle} htmlFor={id}>{p.label}<Req on={p.required} /></label>
      <textarea id={id} style={{ ...inputStyle, minHeight: 90, lineHeight: 1.4 }} value={p.value}
        placeholder={p.placeholder} required={p.required} onChange={(e) => p.onChange(e.target.value)} />
    </div>
  )
}

export function Select(p: {
  label: string; value: string; onChange: (v: string) => void
  options: string[]; required?: boolean; help?: string
  /** Adds an "Other — type it in" escape hatch below the list. */
  allowOther?: boolean
}) {
  const known = p.options.includes(p.value)
  const id = useId()
  return (
    <div style={wrap}>
      <label style={labelStyle} htmlFor={id}>{p.label}<Req on={p.required} /></label>
      <select id={id} style={inputStyle} value={known ? p.value : '__other'}
        onChange={(e) => p.onChange(e.target.value === '__other' ? '' : e.target.value)}>
        {p.options.map((o) => <option key={o}>{o}</option>)}
        {p.allowOther && <option value="__other">Somewhere else…</option>}
      </select>
      {/* A dropdown you cannot escape is worse than no dropdown: the one-off
          answer is exactly the one most worth writing down. */}
      {p.allowOther && !known && (
        <input style={{ ...inputStyle, marginTop: 'var(--s-1)' }} value={p.value} autoFocus
          placeholder="Type it in" required={p.required}
          onChange={(e) => p.onChange(e.target.value)} />
      )}
      {p.help && <p style={helpStyle}>{p.help}</p>}
    </div>
  )
}

export function Toggle(p: { label: string; value: boolean; onChange: (v: boolean) => void; help?: string }) {
  return (
    <div style={wrap}>
      <span style={labelStyle} id={p.label}>{p.label}</span>
      <div role="radiogroup" aria-labelledby={p.label} style={{ display: 'flex', gap: 'var(--s-1)' }}>
        {[true, false].map((v) => (
          <button key={String(v)} type="button" role="radio" aria-checked={p.value === v}
            onClick={() => p.onChange(v)}
            style={{
              flex: 1, minHeight: 'var(--tap)', borderRadius: 'var(--radius-sm)', cursor: 'pointer',
              border: '1px solid ' + (p.value === v ? 'var(--accent)' : 'var(--line)'),
              background: p.value === v ? 'var(--accent)' : 'var(--surface)',
              color: p.value === v ? '#fff' : 'var(--ink-2)', fontWeight: 550,
            }}>
            {v ? 'Yes' : 'No'}
          </button>
        ))}
      </div>
      {p.help && <p style={helpStyle}>{p.help}</p>}
    </div>
  )
}

/** A fact the app worked out, shown back so a wrong guess is visible. */
export function Derived({ k, v }: { k: string; v: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--s-3)', fontSize: 'var(--t-sm)', padding: '2px 0' }}>
      <span style={{ color: 'var(--ink-muted)' }}>{k}</span>
      <span style={{ fontWeight: 550, textAlign: 'right' }}>{v}</span>
    </div>
  )
}

const helpStyle: React.CSSProperties = {
  fontSize: 'var(--t-xs)', color: 'var(--ink-muted)', margin: 'var(--s-1) 0 0', lineHeight: 1.4,
}
